import { PauseCircleFilled, PlayCircleFilled, ToolOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Flex, Form, Input, Radio, Tag, Typography, message } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import {
  myJobs,
  myTimer,
  startTimer,
  stopTimer,
  updateComplaint,
  type JobActivity,
  type JobCard,
  type JobComplaint,
} from './api';
import { ACTIVITY_LABEL, BILLABLE_ACTIVITIES, JOB_STATUS_LABEL, describeSeconds, jobTone } from './status';

const { Text, Title } = Typography;

/**
 * The technician's phone (blueprint §10, P3: "phone-first: big targets,
 * glove-friendly, minimum typing, works on flaky workshop wifi").
 *
 * Four decisions follow from that sentence:
 *
 *  - The running clock is the top of the screen and nothing competes with it.
 *    A technician glancing at a phone on a bench wants one number.
 *  - Start and stop are full-width and 64px tall. Gloves.
 *  - The elapsed time ticks LOCALLY. Polling the server every second over
 *    workshop wifi would show a frozen clock exactly when the wifi is worst.
 *  - Their own jobs are in the offline cache (offline-cache.ts), so the list
 *    survives a dead spot. The job board is not — that is the whole workshop.
 */
export function TechnicianPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activity, setActivity] = useState<JobActivity>('REPAIR');
  const [startingOn, setStartingOn] = useState<JobCard | null>(null);
  const [noting, setNoting] = useState<{ job: JobCard; complaint: JobComplaint } | null>(null);
  const [diagnosis, setDiagnosis] = useState('');
  const [cause, setCause] = useState('');
  const [correction, setCorrection] = useState('');
  const [tick, setTick] = useState(0);

  const { data: timer } = useQuery({
    queryKey: ['my-timer'],
    queryFn: myTimer,
    // Slow poll: the display ticks locally, this only reconciles if somebody
    // stopped the clock from the counter.
    refetchInterval: 60_000,
  });
  const { data: jobs, isLoading } = useQuery({ queryKey: ['my-jobs'], queryFn: myJobs });

  // One local tick per second so the clock moves without the network.
  useEffect(() => {
    if (timer === null || timer === undefined) return;
    const handle = window.setInterval(() => {
      setTick((n) => n + 1);
    }, 1000);
    return () => {
      window.clearInterval(handle);
    };
  }, [timer]);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['my-timer'] });
    void queryClient.invalidateQueries({ queryKey: ['my-jobs'] });
    void queryClient.invalidateQueries({ queryKey: ['job-card'] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const start = useMutation({
    mutationFn: (jobUid: string) => startTimer(jobUid, { activity }),
    onSuccess: () => {
      refresh();
      setStartingOn(null);
    },
    // The API's refusal names the job their clock is already on. Show it.
    onError: (e: unknown) => {
      fail(e, t('workshop.startFailed'));
    },
  });

  const stop = useMutation({
    mutationFn: stopTimer,
    onSuccess: (result) => {
      refresh();
      void message.success(t('workshop.stopped', { duration: result.description }));
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.stopFailed'));
    },
  });

  const saveFindings = useMutation({
    mutationFn: () =>
      updateComplaint(noting?.complaint.uid ?? '', {
        ...(diagnosis.trim().length > 0 ? { diagnosis: diagnosis.trim() } : {}),
        ...(cause.trim().length > 0 ? { cause: cause.trim() } : {}),
        ...(correction.trim().length > 0 ? { correction: correction.trim() } : {}),
      }),
    onSuccess: () => {
      refresh();
      setNoting(null);
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.findingsFailed'));
    },
  });

  const elapsed = timer === null || timer === undefined ? 0 : timer.elapsedSeconds + tick;

  return (
    <Flex vertical gap={16}>
      {timer !== null && timer !== undefined ? (
        <Card
          // The one thing a technician looks at. Nothing competes with it.
          styles={{ body: { padding: 20 } }}
        >
          <Flex vertical gap={12} align="center">
            <Text type="secondary">{t('workshop.runningOn', { jobNo: timer.jobNo })}</Text>
            <Title level={1} style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {describeSeconds(elapsed)}
            </Title>
            <Tag color={timer.activity === 'WAIT' ? 'warning' : 'processing'}>
              {ACTIVITY_LABEL[timer.activity]}
            </Tag>
            {timer.activity === 'WAIT' && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('workshop.waitNotBilled')}
              </Text>
            )}
            <Button
              danger
              type="primary"
              size="large"
              block
              icon={<PauseCircleFilled />}
              style={{ height: 64, fontSize: 18 }}
              loading={stop.isPending}
              onClick={() => {
                stop.mutate();
              }}
            >
              {t('workshop.stop')}
            </Button>
          </Flex>
        </Card>
      ) : (
        <Alert type="info" showIcon message={t('workshop.noTimer')} />
      )}

      {!isLoading && (jobs ?? []).length === 0 && (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('workshop.noJobs')} />
        </Card>
      )}

      {(jobs ?? []).map((job) => {
        const mine = timer?.jobCardUid === job.uid;
        return (
          <Card
            key={job.uid}
            title={
              <Flex vertical gap={2}>
                <Flex align="center" gap={8} wrap>
                  <Text strong style={{ fontSize: 18 }}>
                    {orDash(job.plateNo)}
                  </Text>
                  <Tag color={jobTone(job.status)}>{JOB_STATUS_LABEL[job.status]}</Tag>
                  {job.isComeback && <Tag color="error">{t('workshop.comeback')}</Tag>}
                </Flex>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {job.jobNo} · {orDash(job.model)}
                </Text>
              </Flex>
            }
          >
            <Flex vertical gap={12}>
              {job.complaints.map((complaint) => (
                <Card
                  key={complaint.uid}
                  size="small"
                  styles={{ body: { padding: 12 } }}
                  onClick={() => {
                    setDiagnosis(complaint.diagnosis ?? '');
                    setCause(complaint.cause ?? '');
                    setCorrection(complaint.correction ?? '');
                    setNoting({ job, complaint });
                  }}
                  hoverable
                >
                  <Flex vertical gap={4}>
                    <Text strong>{complaint.complaint}</Text>
                    {complaint.correction === null ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('workshop.tapToRecord')}
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {complaint.correction}
                      </Text>
                    )}
                  </Flex>
                </Card>
              ))}

              {mine ? (
                <Text type="secondary">{t('workshop.clockRunningHere')}</Text>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<PlayCircleFilled />}
                  style={{ height: 64, fontSize: 18 }}
                  onClick={() => {
                    setActivity('REPAIR');
                    setStartingOn(job);
                  }}
                >
                  {t('workshop.start')}
                </Button>
              )}

              <Button
                icon={<ToolOutlined />}
                size="large"
                block
                onClick={() => {
                  void navigate(`/workshop/job-cards/${job.uid}`);
                }}
              >
                {t('workshop.openJob')}
              </Button>
            </Flex>
          </Card>
        );
      })}

      <ActionDrawer
        open={startingOn !== null}
        title={t('workshop.startOn', { plate: orDash(startingOn?.plateNo) })}
        submitLabel={t('workshop.start')}
        submitting={start.isPending}
        onClose={() => {
          setStartingOn(null);
        }}
        onSubmit={() => {
          if (startingOn === null) return;
          start.mutate(startingOn.uid);
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('workshop.whatAreYouDoing')} required>
            {/* Three large targets, not a dropdown. Gloves. */}
            <Radio.Group
              value={activity}
              onChange={(e) => {
                setActivity(e.target.value as JobActivity);
              }}
              size="large"
              buttonStyle="solid"
              optionType="button"
              style={{ width: '100%' }}
              options={BILLABLE_ACTIVITIES.map((a) => ({ value: a, label: ACTIVITY_LABEL[a] }))}
            />
          </Form.Item>
          <Text type="secondary">{t('workshop.oneTimerHelp')}</Text>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={noting !== null}
        title={noting?.complaint.complaint ?? ''}
        submitLabel={t('common.save')}
        submitting={saveFindings.isPending}
        onClose={() => {
          setNoting(null);
        }}
        onSubmit={() => {
          saveFindings.mutate();
        }}
      >
        <Form layout="vertical">
          {/* Three short boxes rather than one long one: the shape of the
              answer prompts for it, which is most of "minimum typing". */}
          <Form.Item label={t('workshop.fields.diagnosis')} help={t('workshop.diagnosisHelp')}>
            <Input.TextArea
              rows={2}
              size="large"
              value={diagnosis}
              onChange={(e) => {
                setDiagnosis(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.cause')} help={t('workshop.causeHelp')}>
            <Input.TextArea
              rows={2}
              size="large"
              value={cause}
              onChange={(e) => {
                setCause(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.correction')} help={t('workshop.correctionHelp')}>
            <Input.TextArea
              rows={2}
              size="large"
              value={correction}
              onChange={(e) => {
                setCorrection(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </Flex>
  );
}
